import { useEffect, useRef } from 'react';
import { useTaskTimerStore } from '@/entities/task/model/stores/task-timer';
import { useTaskTimeoutModalStore } from '@/entities/task/model/stores/task-timeout-modal';
import { useTaskSelectionModalStore } from '@/entities/task/model/stores/task-selection-modal';

export function useTaskTimeMonitor() {
  const tasks = useTaskTimerStore((s) => s.tasks);
  const getCurrentElapsed = useTaskTimerStore((s) => s.getCurrentElapsed);
  const pauseTask = useTaskTimerStore((s) => s.pauseTask);
  const isOpen = useTaskTimeoutModalStore((s) => s.isOpen);
  const open = useTaskTimeoutModalStore((s) => s.open);
  const selectionOpen = useTaskSelectionModalStore((s) => s.isOpen);
  const openedForTaskIdRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const check = async () => {
      if (isOpen || selectionOpen) {
        return;
      }
      if (inFlightRef.current) {
        return;
      }

      const runningTasks = tasks.filter((t) => t.isRunning);

      for (const t of runningTasks) {
        const taskId = String(t.taskId);

        const plannedSec =
          Number(t.plannedSeconds) > 0
            ? Number(t.plannedSeconds)
            : Number(t.plannedMinutes || 0) * 60;

        if (!plannedSec || plannedSec <= 0) {
          continue;
        }

        const elapsedMs = getCurrentElapsed(taskId);
        const elapsedSec = Math.floor(Number(elapsedMs) / 1000);

        if (elapsedSec >= plannedSec) {
          if (openedForTaskIdRef.current === taskId) {
            return;
          }
          openedForTaskIdRef.current = taskId;

          inFlightRef.current = true;
          try {
            await pauseTask(taskId, 0);

            // Если за время ожидания пользователь вручную поставил паузу и
            // уже открылась модалка выбора — не перехватываем её.
            if (useTaskSelectionModalStore.getState().isOpen) {
              return;
            }

            // Обычные задачи и встречи: модалка «Продлить/Завершить».
            // Промежуточные дела в общем таймере больше не живут — их таймаут
            // отслеживает useIntermediateTimeMonitor по серверному состоянию.
            open(taskId);
          } catch (e) {
            console.error(e);
          } finally {
            inFlightRef.current = false;
          }
          return;
        }
      }
    };

    const id = window.setInterval(() => {
      void check();
    }, 1000);

    return () => window.clearInterval(id);
  }, [tasks, getCurrentElapsed, pauseTask, isOpen, open, selectionOpen]);
}
